/**
 * Regelversion 6: knappere Wahlen.
 *
 *  - Erste Wahl: Die eigene Partei startet etwas schwächer, und eine Konkurrenzpartei liegt dicht dahinter.
 *  - Wechselstimmung: Bei jeder Bundestagswahl sammelt sich der Unmut über die Regierung bei der
 *    stärksten Oppositionspartei, statt sich gleichmäßig zu verteilen. Je schlechter die Zustimmung,
 *    desto stärker. Auf „Schwer“ deutlich stärker, auf „Leicht“ schwächer. Die Amtsmüdigkeit
 *    (content/settings.ts) wird dafür abgeschwächt, sonst wäre eine dritte Amtszeit kaum zu schaffen.
 *  Die Werte sind mit Bot-Partien abgestimmt: Wiederwahl nach der ersten Legislatur etwa 80 / 70 / 55 %.
 *  - Landtagswahlen: Die Regierungspartei verliert in den Ländern meist ein paar Punkte (Regierungsmalus),
 *    und die Hürde für einen Sieg ist höher.
 * Im Mehrspieler gegeneinander gilt das nicht, dort treten echte Parteien an.
 */
import { DI } from '../content/settings';
import { S, govLegs } from './state';
import { clamp } from './util';
import { since } from './version';

export const raceOn = (): boolean => since(6) && !(S.mp && (S.mp as any).mode === 'versus');

/** Start der ersten Wahl (Version 1–5: 29,5). */
export const FIRST_BASE = 26.5;
/** Abstand der stärksten Konkurrenz zur eigenen Partei bei der ersten Wahl (Punkte, vor dem Zufall). */
export const FIRST_GAP = 3;
/** Wechselstimmung in Prozentpunkten: Grundwert, Korrektur je Legislatur im Amt, je Punkt Zustimmung unter 45, je Schwierigkeit. */
export const SWING = { base: 9.5, perLeg: -2.5, perAppr: .3, diff: [-5, 0, 6] };
/** Version 10: schwächere Bündelung, damit auch kleinere Parteien stark bleiben und Dreierbündnisse möglich sind. */
export const SWING10 = { base: 6.5, perLeg: -1.5, perAppr: .3, diff: [-4, 0, 5], pool: .45 };
/** Version 12: „Schwer“ deutlich schwerer, „Leicht“ etwas leichter (Bot-Partien: Wiederwahl etwa 88 / 75 / 55 %, mit REELECT12). */
export const SWING12 = { ...SWING10, diff: [-4, 0, 9] };
/** Version 12: Auf „Schwer“ verzeihen die Wähler weniger (Abzug bei jeder Wiederwahl). */
export const REELECT12 = [0, 0, -2.5];
/**
 * Version 12, Erfahrungsstufen 1–4: Abzug bei jeder Wiederwahl. Stufe 1 hat keinen Abzug, dort rücken die Ergebnisse
 * stattdessen zur Mitte (campaign.ts, LV1_PULL). Bot-Partien, fleißig / unaufmerksam: Wiederwahl etwa
 * 99/68, 80/58, 73/26, 54/5 %.
 */
export const REELECT_LV = [0, -2, -1, -2.5];
/** Amtsmüdigkeit je Legislatur wird in Version 6 abgeschwächt, weil die Wechselstimmung sie teilweise übernimmt. */
export const FATIGUE_K = .35;
/** Landtagswahlen: Regierungsmalus und Hürden (Version 1–5: kein Malus, 27 halten / 30 erobern). */
export const LTW = { malus: 1.5, hold: 29, take: 32 };

/** Stärkste Oppositionspartei bekommt Stimmen von den kleineren Konkurrenten. */
export function concentrate(sh: Record<string, number>, gain: number) {
  const opp = Object.keys(sh).filter((k) => k !== 'YOU' && !S.partners.includes(k)).sort((a, b) => sh[b] - sh[a]);
  if (!opp.length || gain <= 0) return;
  const main = opp[0], rest = opp.slice(1).filter((k) => sh[k] > 2.5), pool = rest.reduce((s, k) => s + sh[k] - 2.5, 0);
  const g = Math.min(gain, pool * (since(10) ? SWING10.pool : .7)); if (g <= 0) return;
  for (const k of rest) sh[k] -= g * (sh[k] - 2.5) / pool;
  sh[main] += g;
}

/** Stärke der Wechselstimmung bei einer Bundestagswahl nach `legs` Legislaturen im Amt. */
export function swing(appr: number): number {
  const W = since(12) ? SWING12 : since(10) ? SWING10 : SWING;
  return clamp(W.base + W.perLeg * govLegs() + Math.max(0, 45 - appr) * W.perAppr + W.diff[DI()], 0, 22);
}

/**
 * Sonntagsfrage während der Regierungszeit: geschätzte Anteile ohne Zufall (nur Anzeige).
 * Gleiche Rechnung wie bei der Wahl, mit den Grundwerten der Parteien statt gewürfelter Abweichung.
 */
export function sundayPoll(you: number, appr: number, base: Record<string, number>): { id: string; s: number }[] {
  const sh: Record<string, number> = { YOU: you }; let o = 0;
  for (const k in base) { sh[k] = Math.max(2, base[k] - (S.partners.includes(k) ? 1 : 0)); o += sh[k]; }
  const f = Math.max(0, 100 - you) / (o || 1); for (const k in base) sh[k] *= f;
  if (raceOn()) concentrate(sh, swing(appr));
  return Object.keys(sh).map((id) => ({ id, s: Math.round(sh[id] * 10) / 10 })).sort((a, b) => b.s - a.s);
}
